import PropTypes from 'prop-types';
import React, { useCallback, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import Badge from 'components/Badge';
import ButtonV2 from 'components/ButtonV2';
import ModalV2 from 'components/Modal/ModalV2';
import Toaster from 'components/Toaster';
import Icon from 'components/v3/Icon';
import { useGetApiKeys } from '../../gql/useGetApiKeys';
import { useRevokeApiKey } from '../../gql/useRevokeApiKey';
import CreateApiKeyModal from './CreateApiKeyModal';

// Partner API keys for one account: list, create (shown once), revoke.
// Only on a saved account; the add-account form has no account id yet.

const TITLE_ID = 'partner-api-title';

const DATE_FORMAT = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
};

// "Oct 27, 2026". Read in UTC: an expiry is the end of a UTC day.
export const formatDate = (iso) =>
  new Date(iso).toLocaleDateString('en-US', DATE_FORMAT);

const STATUS = {
  active: { label: 'Active', variant: 'success' },
  revoked: { label: 'Revoked', variant: 'error' },
  expired: { label: 'Expired', variant: 'alert' },
};

// A revoked key reads Revoked whatever its expiry.
export const keyStatus = (apiKey, now) => {
  if (apiKey.revoked_at) return STATUS.revoked;
  if (apiKey.expires_at && Date.parse(apiKey.expires_at) <= now) {
    return STATUS.expired;
  }
  return STATUS.active;
};

const revokeToast = (apiKey, result) =>
  result.success
    ? {
        type: 'success',
        title: 'Key revoked',
        message: `${apiKey.name} no longer works.`,
      }
    : { type: 'error', title: 'Error', message: result.error };

const shimmer = keyframes`
  from { background-position: 100% 0; }
  to { background-position: -100% 0; }
`;

const Panel = styled.section`
  margin-bottom: 32px;
  padding: ${({ theme }) => theme.spacing.lg};
  border: 1px solid ${({ theme }) => theme.colors.card.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ theme }) => theme.colors.card.background};
`;

const PanelHead = styled.header`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;

  button {
    width: auto;
    flex-shrink: 0;
  }
`;

const PanelTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const PanelText = styled.p`
  margin: 4px 0 0;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textGray.secondary};
`;

const KeyList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 0;
  padding: 0;
  list-style: none;
`;

const KeyRow = styled.li`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.background.overlay};

  button {
    width: auto;
    flex-shrink: 0;
  }
`;

const KeyTile = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) =>
    theme.colors.primaryOpacity(theme.name === 'dark' ? 0.15 : 0.08)};
`;

const KeyMain = styled.div`
  flex: 1;
  min-width: 0;
`;

const KeyHeading = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const KeyCode = styled.code`
  font-family: ${({ theme }) => theme.fonts.monospace};
  font-size: 13px;
  overflow-wrap: anywhere;
`;

const KeyPrefix = styled(KeyCode)`
  display: block;
  margin-top: 4px;
  color: ${({ theme }) => theme.colors.textGray.secondary};
`;

const KeyMeta = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 14px;
  margin-top: 8px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textGray.secondary};
`;

const Scopes = styled.span`
  display: inline-flex;
  gap: 6px;
`;

const Notice = styled.p`
  margin: 0;
  padding: 24px 16px;
  text-align: center;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textGray.secondary};
  border: 1px dashed ${({ theme }) => theme.colors.card.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
`;

const Skeletons = styled.div`
  display: grid;
  gap: 12px;
`;

const SkeletonRow = styled.div`
  height: 72px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) =>
    theme.name === 'dark'
      ? 'linear-gradient(90deg, rgba(255,255,255,0.04), rgba(255,255,255,0.09), rgba(255,255,255,0.04))'
      : 'linear-gradient(90deg, #f1f3f6, #e4e8ee, #f1f3f6)'};
  background-size: 200% 100%;
  animation: ${shimmer} 1.4s ease-in-out infinite;
`;

const ConfirmText = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.textGray.secondary};
`;

const KeyIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <circle cx="7.5" cy="15.5" r="4.5" />
    <path d="M10.7 12.3 21 2" />
    <path d="m15.5 7.5 3 3" />
    <path d="m18 5 2 2" />
  </svg>
);

const ApiKeyRow = ({ apiKey, now, onRevoke }) => {
  const status = keyStatus(apiKey, now);
  return (
    <KeyRow>
      <KeyTile>
        <KeyIcon />
      </KeyTile>
      <KeyMain>
        <KeyHeading>
          {apiKey.name}
          <Badge size="small" variant={status.variant}>
            {status.label}
          </Badge>
        </KeyHeading>
        <KeyPrefix>{apiKey.key_prefix}…</KeyPrefix>
        <KeyMeta>
          <Scopes>
            {apiKey.scopes.map((scope) => (
              <Badge key={scope} size="small" variant="neutral">
                {scope}
              </Badge>
            ))}
          </Scopes>
          <span>Created {formatDate(apiKey.created_at)}</span>
          <span>
            Last used{' '}
            {apiKey.last_used_at ? formatDate(apiKey.last_used_at) : 'never'}
          </span>
          {apiKey.expires_at && (
            <span>Expires {formatDate(apiKey.expires_at)}</span>
          )}
        </KeyMeta>
      </KeyMain>
      {status === STATUS.active && (
        <ButtonV2
          danger
          type="button"
          aria-label={`Revoke ${apiKey.name}`}
          onClick={() => onRevoke(apiKey)}
        >
          Revoke
        </ButtonV2>
      )}
    </KeyRow>
  );
};

const apiKeyShape = PropTypes.shape({
  id: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  key_prefix: PropTypes.string.isRequired,
  scopes: PropTypes.arrayOf(PropTypes.string).isRequired,
  created_at: PropTypes.string.isRequired,
  last_used_at: PropTypes.string,
  expires_at: PropTypes.string,
  revoked_at: PropTypes.string,
});

ApiKeyRow.propTypes = {
  apiKey: apiKeyShape.isRequired,
  now: PropTypes.number.isRequired,
  onRevoke: PropTypes.func.isRequired,
};

const KeysBody = ({ apiKeys, loading, error, onRevoke }) => {
  if (error) {
    return <Notice role="alert">Could not load API keys. {error}</Notice>;
  }
  if (loading && 0 === apiKeys.length) {
    return (
      <Skeletons role="status" aria-label="Loading API keys">
        <SkeletonRow />
        <SkeletonRow />
      </Skeletons>
    );
  }
  if (0 === apiKeys.length) return <Notice>No API keys yet</Notice>;
  const now = Date.now();
  return (
    <KeyList aria-label="API keys">
      {apiKeys.map((apiKey) => (
        <ApiKeyRow
          key={apiKey.id}
          apiKey={apiKey}
          now={now}
          onRevoke={onRevoke}
        />
      ))}
    </KeyList>
  );
};

KeysBody.propTypes = {
  apiKeys: PropTypes.arrayOf(apiKeyShape).isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  onRevoke: PropTypes.func.isRequired,
};

const RevokeKeyModal = ({ apiKey, accountId, onCancel, onDone }) => {
  const { revokeApiKey, loading } = useRevokeApiKey(accountId);
  const revoke = async () => onDone(apiKey, await revokeApiKey(apiKey.id));
  return (
    <ModalV2
      isOpen
      onClose={onCancel}
      title="Revoke this key?"
      maxWidth="440px"
      footer={
        <>
          <ButtonV2 type="button" onClick={onCancel}>
            Cancel
          </ButtonV2>
          <ButtonV2 danger type="button" onClick={revoke} disabled={loading}>
            {loading ? 'Revoking…' : 'Revoke key'}
          </ButtonV2>
        </>
      }
    >
      <ConfirmText>
        <strong>{apiKey.name}</strong> (<KeyCode>{apiKey.key_prefix}…</KeyCode>)
        stops working at once, and every request made with it is refused. This
        cannot be undone.
      </ConfirmText>
    </ModalV2>
  );
};

RevokeKeyModal.propTypes = {
  apiKey: apiKeyShape.isRequired,
  accountId: PropTypes.number.isRequired,
  onCancel: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
};

const ApiKeysPanel = ({ accountId }) => {
  const { apiKeys, loading, error } = useGetApiKeys(accountId);
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState(null);
  const [toast, setToast] = useState(null);

  const closeToast = useCallback(() => setToast(null), []);
  const showError = useCallback(
    (message) => setToast({ type: 'error', title: 'Error', message }),
    []
  );
  const finishRevoke = (apiKey, result) => {
    setRevoking(null);
    setToast(revokeToast(apiKey, result));
  };

  return (
    <Panel aria-labelledby={TITLE_ID}>
      <PanelHead>
        <div>
          <PanelTitle id={TITLE_ID}>Partner API</PanelTitle>
          <PanelText>
            Keys a promoter&apos;s engineers use to pull this account&apos;s
            customers and sales from the partner API.
          </PanelText>
        </div>
        <ButtonV2
          elevated
          type="button"
          icon={<Icon name="plus" size={16} />}
          onClick={() => setCreating(true)}
        >
          Create key
        </ButtonV2>
      </PanelHead>
      <KeysBody
        apiKeys={apiKeys}
        loading={loading}
        error={error}
        onRevoke={setRevoking}
      />
      {creating && (
        <CreateApiKeyModal
          accountId={accountId}
          onClose={() => setCreating(false)}
          onError={showError}
        />
      )}
      {revoking && (
        <RevokeKeyModal
          apiKey={revoking}
          accountId={accountId}
          onCancel={() => setRevoking(null)}
          onDone={finishRevoke}
        />
      )}
      {toast && (
        <Toaster isOpen onClose={closeToast} duration={5000} {...toast} />
      )}
    </Panel>
  );
};

ApiKeysPanel.propTypes = {
  accountId: PropTypes.number.isRequired,
};

const ApiKeysSection = ({ accountId }) =>
  accountId ? <ApiKeysPanel accountId={Number(accountId)} /> : null;

ApiKeysSection.propTypes = {
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default ApiKeysSection;
